import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useAntiCheat — one hook, one policy object, all the in-exam listeners.
 *
 * Attaches only what the drive's policy asks for and reports what it sees. It
 * makes no judgements: the SERVER decides what a violation costs, whether to
 * warn, and whether to end the exam. This hook's opinion would be trivially
 * patchable by anyone who opened devtools.
 *
 * Warnings surface as in-app banners, never window.alert — a native dialog
 * steals focus, blocks the timer thread and drops fullscreen in some browsers,
 * so it would CAUSE the violation it is reporting.
 *
 * Signals are documented in docs/public-exams/EXAM_SECURITY_MODEL.md §Layer 3.
 */

const noop = () => {};

export default function useAntiCheat({ policy, active, onEvent, onFullscreenChange }) {
  const [fullscreenActive, setFullscreenActive] = useState(false);
  const [warning, setWarning] = useState(null);

  const onEventRef = useRef(onEvent || noop);
  const activeRef = useRef(active);
  const suppressRef = useRef(false); // set while submitting, so our own teardown isn't a violation

  useEffect(() => { onEventRef.current = onEvent || noop; }, [onEvent]);
  useEffect(() => { activeRef.current = active; }, [active]);

  const report = useCallback((type, meta = {}) => {
    if (!activeRef.current || suppressRef.current) return;
    onEventRef.current(type, {
      ...meta,
      // A client id makes the report idempotent server-side: a retry over a
      // flaky network must not inflate the candidate's violation count.
      clientEventId: `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      clientAt: new Date().toISOString(),
    });
  }, []);

  /** Called by the runner when submitting, so teardown does not fire signals. */
  const suppress = useCallback((on = true) => { suppressRef.current = on; }, []);

  const fullscreenSupported =
    typeof document !== "undefined"
    && !!(document.documentElement.requestFullscreen || document.documentElement.webkitRequestFullscreen);

  const enterFullscreen = useCallback(async () => {
    const el = document.documentElement;
    try {
      if (el.requestFullscreen) await el.requestFullscreen();
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
    } catch {
      // The browser refused or the candidate dismissed it. The gate stays up;
      // never trap them in a loop of failed attempts.
    }
  }, []);

  const exitFullscreen = useCallback(async () => {
    try {
      if (document.exitFullscreen) await document.exitFullscreen();
      else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
    } catch { /* noop */ }
  }, []);

  // ── Tab switch / visibility ────────────────────────────────────────
  useEffect(() => {
    if (!active) return undefined;
    const handler = () => { if (document.hidden) report("tabSwitch"); };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [active, report]);

  // ── Window blur ────────────────────────────────────────────────────
  useEffect(() => {
    if (!active) return undefined;
    const cfg = policy?.signals?.windowBlur;
    if (!cfg) return undefined;
    const handler = () => {
      // Blur fires alongside visibilitychange on a tab switch. Reporting both
      // would double-count one action, so skip when the tab is already hidden.
      if (document.hidden) return;
      report("windowBlur");
    };
    window.addEventListener("blur", handler);
    return () => window.removeEventListener("blur", handler);
  }, [active, policy, report]);

  // ── Fullscreen ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!policy?.fullscreenRequired || !fullscreenSupported) {
      if (policy?.fullscreenRequired && !fullscreenSupported) {
        // Never lock a candidate out of an exam their browser cannot satisfy.
        // Recorded so a reviewer knows the gate was not in force.
        report("fullscreenUnsupported");
      }
      return undefined;
    }

    const sync = () => {
      const on = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setFullscreenActive(on);
      onFullscreenChange?.(on);
      if (on) report("fullscreenEntered");
      else if (activeRef.current) report("fullscreenExit");
    };

    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    const initial = !!(document.fullscreenElement || document.webkitFullscreenElement);
    setFullscreenActive(initial);
    onFullscreenChange?.(initial);

    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policy?.fullscreenRequired, fullscreenSupported]);

  // ── Copy / paste / cut ─────────────────────────────────────────────
  useEffect(() => {
    if (!active || !policy?.disableCopyPaste) return undefined;

    const block = (e) => {
      // A candidate must always be able to edit their own typing. Blocking
      // paste inside a text answer is the point; blocking copy of their own
      // draft is just cruel and stops nothing.
      const tag = (e.target?.tagName || "").toLowerCase();
      const editable = tag === "input" || tag === "textarea" || e.target?.isContentEditable;
      if (editable && e.type === "copy") return;
      e.preventDefault();
      report("copyPaste", { action: e.type });
    };

    document.addEventListener("copy", block);
    document.addEventListener("cut", block);
    document.addEventListener("paste", block);
    return () => {
      document.removeEventListener("copy", block);
      document.removeEventListener("cut", block);
      document.removeEventListener("paste", block);
    };
  }, [active, policy?.disableCopyPaste, report]);

  // ── Right click ────────────────────────────────────────────────────
  useEffect(() => {
    if (!active || !policy?.disableRightClick) return undefined;
    const handler = (e) => { e.preventDefault(); report("rightClick"); };
    document.addEventListener("contextmenu", handler);
    return () => document.removeEventListener("contextmenu", handler);
  }, [active, policy?.disableRightClick, report]);

  // ── Print ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!active || !policy?.blockPrint) return undefined;
    const onBeforePrint = () => report("printAttempt");
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
        e.preventDefault();
        report("printAttempt", { via: "keyboard" });
      }
    };
    window.addEventListener("beforeprint", onBeforePrint);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("beforeprint", onBeforePrint);
      document.removeEventListener("keydown", onKey);
    };
  }, [active, policy?.blockPrint, report]);

  // ── Multi-monitor ──────────────────────────────────────────────────
  useEffect(() => {
    if (!active || !policy?.signals?.multiMonitor) return undefined;
    // `isExtended` is Chromium-only and undefined elsewhere. Absent means
    // "we don't know", which must never be reported as "a second screen".
    if (typeof window.screen?.isExtended !== "boolean") return undefined;

    const check = () => {
      if (window.screen.isExtended) {
        report("multiMonitor", { screens: window.screen.isExtended ? 2 : 1 });
      }
    };
    check();
    const id = setInterval(check, 60000);
    return () => clearInterval(id);
  }, [active, policy?.signals?.multiMonitor, report]);

  // ── Devtools (heuristic) ───────────────────────────────────────────
  useEffect(() => {
    if (!active || !policy?.signals?.devtools) return undefined;

    let reported = false;
    const check = () => {
      if (reported) return;
      // Docked devtools shrink the viewport relative to the window. This is a
      // HEURISTIC — it misfires on some zoom levels and browser chrome, which
      // is exactly why the policy defaults this signal to `log` and never to
      // autoSubmit anywhere.
      const wGap = window.outerWidth - window.innerWidth;
      const hGap = window.outerHeight - window.innerHeight;
      if (wGap > 220 || hGap > 220) {
        reported = true;
        report("devtools", { wGap, hGap });
      }
    };
    const id = setInterval(check, 3000);
    return () => clearInterval(id);
  }, [active, policy?.signals?.devtools, report]);

  // ── Reload / close ─────────────────────────────────────────────────
  useEffect(() => {
    if (!active) return undefined;
    const handler = (e) => {
      if (suppressRef.current) return;
      // The browser shows its own generic confirm; the string is ignored by
      // every modern engine but must be set for the prompt to appear at all.
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [active]);

  // Report a reload AFTER it happens — a beforeunload report never lands.
  useEffect(() => {
    if (!active) return;
    try {
      const nav = performance.getEntriesByType?.("navigation")?.[0];
      if (nav?.type === "reload") report("refresh");
    } catch { /* noop */ }
    // Once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Text selection on the paper is locked by the runner itself (the question and
  // its options render `tw-select-none` when the policy blocks copying), so the
  // candidate's own answer fields stay selectable.

  /** Show a server-authored warning. The wording comes from the policy, not here. */
  const showWarning = useCallback((text) => {
    if (!text) return;
    setWarning({ text, id: Date.now() });
  }, []);

  const dismissWarning = useCallback(() => setWarning(null), []);

  useEffect(() => {
    if (!warning) return undefined;
    const id = setTimeout(() => setWarning(null), 8000);
    return () => clearTimeout(id);
  }, [warning]);

  return {
    fullscreenActive,
    fullscreenSupported,
    enterFullscreen,
    exitFullscreen,
    warning,
    showWarning,
    dismissWarning,
    suppress,
    report,
  };
}
